import { FormArray, FormControl, FormGroup, Validators } from "@angular/forms";
import { BmItemForm, BmMgmFeePcntForm } from "../../../pojo/bm-form";
import { BmMgmFeePcnt, BmPeriodEnum } from "../../../pojo/bm-classes";
import { BmCalcType } from "../../../pojo/bm-enums";
import { BmCalcHelperService } from "../../general/bm-calc-helper.service";

/**
 * SERVICE to manage row MGM FEE PCNT logic in Management Fees Group
 *
 * 1) If the user changes YEAR:
 *    - That YEAR item becomes MANUAL
 *    - All four QUARTER items for that year become (yearValue / 4), calcType=CALCULATED
 *
 * 2) If the user changes a QUARTER:
 *    - That quarter becomes MANUAL
 *    - The YEAR item becomes the sum of all four quarters, and CALCULATED
 *
 * 3) If standardValue changes:
 *    - We only overwrite YEAR items that are GENERATED
 *    - We set them to the new standardValue, keep them as GENERATED
 *    - QUARTER items standardValue / 4
 *
 * 4) YEAR items are editable, the user can edit them directly.
 */
export class MgmFeePcntFormService {
  public static createMgmFeePcntForm(mgmFeePcnt: BmMgmFeePcnt): FormGroup<BmMgmFeePcntForm> {
    const mgmFeePcntForm = new FormGroup<BmMgmFeePcntForm>({
      standardValue: new FormControl(mgmFeePcnt?.standardValue ?? 0, { validators: Validators.max(100) }),
      items: new FormArray(mgmFeePcnt.items.map((data) => BmCalcHelperService.createBmItemForm(data))),
    });
    BmCalcHelperService.disableItems(mgmFeePcntForm.controls.items);
    return mgmFeePcntForm;
  }

  /**
   * If user change STANDARD VALUE =>
   * For each YEAR item that are GENERATED => change its value to newStdVal, calcType to GENERATED
   * For that same year, all QUARTER items become newStdVal / 4, calcType to GENERATED
   */
  public static handleStandardValueChange(itemsFa: FormArray<FormGroup<BmItemForm>>, newStdVal: number): void {
    const itemsByYear = BmCalcHelperService.groupItemsByYear(itemsFa);

    itemsByYear.forEach(({ yearFG, quartersFG }) => {
      if (yearFG.getRawValue().calcType !== BmCalcType.GENERATED) {
        return;
      }

      BmCalcHelperService.patchAndDisable(yearFG, newStdVal, BmCalcType.GENERATED);

      quartersFG.forEach((quarter) => {
        BmCalcHelperService.patchAndDisable(quarter, newStdVal / 4, BmCalcType.GENERATED);
      });

    });
  }

  public static handleItemsChange(itemsFa: FormArray<FormGroup<BmItemForm>>): void {

    itemsFa.controls.forEach((itemFG) => {

      if (!itemFG.dirty) {
        // not changed
        return;
      }
      const period = itemFG.value.period;
      const quarters = BmCalcHelperService.findQuarterItemsInSelectedYear(itemsFa, period.yearNumber);
      const yearFG = BmCalcHelperService.findYearItemInSelectedYear(itemsFa, period.yearNumber);

      if (period.type === BmPeriodEnum.QUARTER) {
        MgmFeePcntFormService.handleQuarterEdit(yearFG, quarters);
      } else {
        MgmFeePcntFormService.handleYearEdit(yearFG, quarters);
      }
      itemFG.markAsPristine();

    });
  }


  /**
   * If the user edits a YEAR =>
   *    - that YEAR is MANUAL
   *    - all QUARTER items for the same yearNumber are overwritten => yearValue/4, calcType=CALCULATED
   */
  public static handleYearEdit(yearFG: FormGroup<BmItemForm>, quarters: FormGroup<BmItemForm>[]): void {
    const yearVal = yearFG.controls.itemValue.value;
    quarters.forEach((quarter) => {
      BmCalcHelperService.patchAndDisable(quarter, yearVal / 4, BmCalcType.CALCULATED);
    });
  }

  /**
   * If the user edits a QUARTER =>
   *    - that QUARTER => MANUAL
   *    - the YEAR => sum of all 4 quarters => CALCULATED
   */
  public static handleQuarterEdit(yearFG: FormGroup<BmItemForm>, quartersFG: FormGroup<BmItemForm>[]): void {
    const sumOfQuarters = BmCalcHelperService.calculateSumOfQuarterValues(quartersFG);
    BmCalcHelperService.patchAndDisable(yearFG, sumOfQuarters, BmCalcType.CALCULATED);
  }
}
